/**
 * Country detail sidebar - slide-in panel with tabbed content.
 * Tabs: Overview | Regulations | Contacts | Resources
 */
import type { CountryData } from '../types';
import { STATUS_CONFIG } from '../types';
import { isAdminMode } from '../admin/state';
import { clearCountryURL } from '../url';
import { resourcesByCountry } from '../data/resources-data';
import { getProjectsByCountry, PROJECT_TYPE_CONFIG } from '../map/drone-projects';
import type { DroneProject } from '../map/drone-projects';
import type { OpportunityItem } from './news-panel';
import { isAfricanCountry } from './news-panel';

const TABS = ['Overview', 'Regulations', 'Contacts', 'Resources', 'Tenders'] as const;

// ── Shared helpers ──────────────────────────────────────────────────────────

/** Strip HTML tags and return trimmed text content. */
function stripTags(html: string): string {
  return (html || '').replace(/<[^>]*>/g, '').replace(/&[a-z]+;/gi, ' ').replace(/\s+/g, ' ').trim();
}

/** Placeholder patterns that mean "no real pilot contact exists". */
const PILOT_PLACEHOLDER_RE = /no publicly listed operator|no verified pilots|not available/i;

/** True when the country has a real, non-placeholder verified pilot contact. */
function hasRealPilotContact(c: CountryData): boolean {
  const text = stripTags(c.contacts.verified_pilots_html || '') || (c.contacts.verified_pilots || '').trim();
  if (!text) return false;
  if (PILOT_PLACEHOLDER_RE.test(text)) return false;
  return true;
}

/** Freshness line: reviewed date if present, otherwise a "baseline imported" caveat. */
function freshnessLine(regs: CountryData['regulations']): string {
  if (regs.last_reviewed) {
    return `<div class="data-updated">Updated ${formatUpdatedDate(regs.last_reviewed)}</div>`;
  }
  if (regs.last_updated) {
    return `<div class="data-updated">Baseline data imported ${formatUpdatedDate(regs.last_updated)}, not yet re-verified</div>`;
  }
  return '';
}

/** Render a URL as a link whose visible text is just the hostname (no protocol, no www). */
function urlAsHostnameLink(u: string): string {
  let host = u;
  try {
    host = new URL(u).hostname;
  } catch {
    host = u.replace(/^https?:\/\//i, '').split('/')[0];
  }
  host = host.replace(/^www\./i, '');
  return `<a href="${u}" target="_blank" rel="noopener">${host}</a>`;
}

// ── ATO data types + fetch ─────────────────────────────────────────────────

interface ATOEntry {
  name: string;
  city: string | null;
  url: string | null;
  email: string | null;
  phone: string | null;
  approval_ref: string | null;
  source_authority: string | null;
  notes: string | null;
}

interface FeaturedPartner {
  country: string;
  org: string;
  url: string;
  blurb?: string;
  logo?: string | null;
  since?: string;
}

let featuredCache: FeaturedPartner[] | null = null;
async function fetchFeaturedPartners(iso2: string): Promise<FeaturedPartner[]> {
  if (!featuredCache) {
    try {
      const res = await fetch('/data/featured_partners.json', { cache: 'no-cache' });
      const raw = res.ok ? await res.json() : [];
      featuredCache = Array.isArray(raw)
        ? raw.filter((p: any) => p && !p._instructions && p.org && p.url && p.country)
        : [];
    } catch {
      featuredCache = [];
    }
  }
  return featuredCache.filter(p => p.country.toUpperCase() === iso2.toUpperCase());
}

let atoCache: Record<string, ATOEntry[]> | null = null;
async function fetchATOs(iso2: string): Promise<ATOEntry[]> {
  if (!atoCache) {
    try {
      const res = await fetch('/data/atos.json');
      atoCache = res.ok ? await res.json() : {};
    } catch {
      atoCache = {};
    }
  }
  return atoCache![iso2.toUpperCase()] || [];
}

// Module-level cache shared across sidebar instances (one fetch per session)
let sidebarOppsCache: OpportunityItem[] | null = null;
async function loadAllOpportunities(): Promise<OpportunityItem[]> {
  if (sidebarOppsCache) return sidebarOppsCache;
  try {
    const res = await fetch('/data/opportunities.json', { cache: 'no-cache' });
    if (!res.ok) return [];
    const data = await res.json();
    const today = new Date().toISOString().slice(0, 10);
    sidebarOppsCache = (data.opportunities || []).filter(
      (o: OpportunityItem) => !o.deadline || o.deadline >= today
    );
    return sidebarOppsCache!;
  } catch {
    return [];
  }
}

/** Month name/abbreviation to 0-based index. */
const MONTHS: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

/**
 * Try to parse a deadline date out of an opportunity title.
 * Handles "Deadline 04-May-26", "Deadline: 4 May 2026", "closing 04/05/2026".
 * Returns an ISO YYYY-MM-DD string, or null if nothing parseable is found.
 */
function parseDeadlineFromTitle(title: string): string | null {
  if (!title) return null;
  const t = title;

  // "04-May-26" or "4 May 2026" (day, month name, year)
  const named = /(\d{1,2})[\s\-./]+([A-Za-z]{3,9})[\s\-./]+(\d{2,4})/.exec(t);
  if (named) {
    const day = parseInt(named[1], 10);
    const mon = MONTHS[named[2].slice(0, 3).toLowerCase()];
    if (mon !== undefined) {
      let year = parseInt(named[3], 10);
      if (year < 100) year += 2000;
      return isoFromParts(year, mon, day);
    }
  }

  // "04/05/2026" or "04-05-2026" (day/month/year, numeric), only near a deadline cue.
  if (/deadline|closing|closes|due/i.test(t)) {
    const numeric = /(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})/.exec(t);
    if (numeric) {
      const day = parseInt(numeric[1], 10);
      const mon = parseInt(numeric[2], 10) - 1;
      let year = parseInt(numeric[3], 10);
      if (year < 100) year += 2000;
      if (mon >= 0 && mon <= 11) return isoFromParts(year, mon, day);
    }
  }
  return null;
}

function isoFromParts(year: number, monthIdx: number, day: number): string | null {
  if (!year || day < 1 || day > 31) return null;
  const mm = String(monthIdx + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

/**
 * True when a tender title is an off-topic works/construction notice
 * (borehole, school block, etc.) with no drone/geospatial relevance.
 */
function isIrrelevantTender(title: string): boolean {
  const t = title || '';
  const worksRe = /borehole|drilling of|renovation of|construction of\s+(a\s+)?(school|classroom|office|toilet|block)/i;
  const droneRe = /drone|uav|rpas|aerial|mapping|lidar|survey|geospatial|imagery/i;
  return worksRe.test(t) && !droneRe.test(t);
}

/** Filter the loaded opportunities for a given country. Matches case-insensitively
 *  on the country name, plus common aliases (long ↔ short forms, abbreviations).
 *  Also includes pan-African ("Africa") tenders when the country is on the African
 *  continent, so a Kenyan reader sees both Kenya-specific and Africa-wide opportunities. */
function tendersForCountry(all: OpportunityItem[], countryName: string): OpportunityItem[] {
  const target = countryName.trim().toLowerCase();
  // Bidirectional aliases. Each entry lists every variant a tender feed might use
  // for the same country; matching is symmetric (lookup works in both directions).
  const aliasGroups: string[][] = [
    ['democratic republic of the congo', 'drc', 'dr congo', 'congo-kinshasa'],
    ['republic of the congo', 'congo', 'congo-brazzaville'],
    ['united republic of tanzania', 'tanzania'],
    ['united kingdom', 'uk', 'great britain', 'britain'],
    ['united states', 'usa', 'us', 'united states of america'],
    ['united arab emirates', 'uae'],
    ['russian federation', 'russia'],
    ["lao people's democratic republic", 'laos', 'lao'],
    ["cote d'ivoire", "côte d'ivoire", 'ivory coast'],
    ['cape verde', 'cabo verde'],
    ['eswatini', 'swaziland'],
    ['myanmar', 'burma'],
    ['north korea', "democratic people's republic of korea", 'dprk'],
    ['south korea', 'republic of korea', 'korea'],
  ];
  const targetSet = new Set([target]);
  for (const group of aliasGroups) {
    if (group.includes(target)) group.forEach(g => targetSet.add(g));
  }
  // Set of African country names (lowercased) — used to surface pan-African tenders.
  const AFRICAN_COUNTRIES = new Set([
    'algeria','angola','benin','botswana','burkina faso','burundi','cameroon','cape verde',
    'cabo verde','central african republic','chad','comoros','congo','republic of the congo',
    'democratic republic of the congo','drc','djibouti','egypt','equatorial guinea','eritrea',
    'eswatini','swaziland','ethiopia','gabon','gambia','ghana','guinea','guinea-bissau',
    "côte d'ivoire","cote d'ivoire",'ivory coast','kenya','lesotho','liberia','libya',
    'madagascar','malawi','mali','mauritania','mauritius','morocco','mozambique','namibia',
    'niger','nigeria','rwanda','sao tome and principe','senegal','seychelles','sierra leone',
    'somalia','south africa','south sudan','sudan','tanzania','united republic of tanzania',
    'togo','tunisia','uganda','western sahara','zambia','zimbabwe',
  ]);
  const isAfrican = AFRICAN_COUNTRIES.has(target);
  const todayIso = new Date().toISOString().slice(0, 10);
  return all.filter(o => {
    const c = (o.country || '').trim().toLowerCase();
    const countryMatch = targetSet.has(c) ||
      // Pan-African opportunities surface on every African country sidebar.
      (isAfrican && (c === 'africa' || c === 'pan-african' || c === 'african union' || c === 'au'));
    if (!countryMatch) return false;

    // Drop off-topic works/construction notices with no drone/geospatial angle.
    if (isIrrelevantTender(o.title || '')) return false;

    // If the structured deadline is empty, try to parse one from the title;
    // exclude the item when that parsed deadline has already passed.
    if (!o.deadline) {
      const parsed = parseDeadlineFromTitle(o.title || '');
      if (parsed && parsed < todayIso) return false;
    }
    return true;
  });
}

/* ── Instant drone flight authorization systems by country ───────────────── */
interface AuthSystem {
  icon: string;
  label: string;
  description: string;
  url: string;
  gradient: string;   // CSS gradient for the banner
  hoverGlow: string;  // box-shadow color on hover
}

const AUTH_SYSTEMS: Record<string, AuthSystem> = {
  US: {
    icon: '\u{1F6EB}',  // takeoff emoji
    label: 'FAA LAANC',
    description: 'Register and approve your drone flight immediately with the',
    url: 'https://www.aloft.ai/feature/laanc/',
    gradient: 'linear-gradient(135deg, #1a365d, #2563eb)',
    hoverGlow: 'rgba(37, 99, 235, 0.3)',
  },
  IN: {
    icon: '\u{1F4F1}',  // phone emoji
    label: 'DGCA Digital Sky',
    description: 'Get instant Green Zone flight authorization via',
    url: 'https://digitalsky.dgca.gov.in/',
    gradient: 'linear-gradient(135deg, #1a4731, #059669)',
    hoverGlow: 'rgba(5, 150, 105, 0.3)',
  },
  AU: {
    icon: '\u{1F30F}',  // globe emoji
    label: 'CASA Automated Auth',
    description: 'Get near-instant airspace authorization at major airports via',
    url: 'https://www.casa.gov.au/drones/flight-authorisations/automated-airspace-authorisations',
    gradient: 'linear-gradient(135deg, #4a1d6e, #7c3aed)',
    hoverGlow: 'rgba(124, 58, 237, 0.3)',
  },
  CA: {
    icon: '\u{1F6E9}\u{FE0F}',  // small airplane emoji
    label: 'NAV Drone',
    description: 'Plan flights and request controlled airspace authorization via',
    url: 'https://www.navcanada.ca/en/flight-planning/drone-flight-planning.aspx',
    gradient: 'linear-gradient(135deg, #7f1d1d, #dc2626)',
    hoverGlow: 'rgba(220, 38, 38, 0.3)',
  },
  NZ: {
    icon: '\u{2708}\u{FE0F}',  // airplane emoji
    label: 'AirShare NZ',
    description: 'Request controlled airspace authorization digitally via',
    url: 'https://airshare.co.nz/',
    gradient: 'linear-gradient(135deg, #164e63, #0891b2)',
    hoverGlow: 'rgba(8, 145, 178, 0.3)',
  },
};

let currentCountry: CountryData | null = null;
let activeTab = 0;

/** Initialize sidebar DOM and event listeners */
export function initSidebar(onClose: () => void) {
  const sidebar = document.getElementById('sidebar')!;
  const overlay = document.getElementById('sidebar-overlay')!;

  // Close on overlay click
  overlay.addEventListener('click', () => closeSidebar(onClose));

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sidebar.classList.contains('open')) {
      closeSidebar(onClose);
    }
  });
}

/** Open sidebar with country data */
export function openSidebar(country: CountryData) {
  currentCountry = country;
  activeTab = 0;
  renderSidebar();

  // Close resources panel if open (avoid circular import — toggle DOM directly)
  document.getElementById('resources-panel')?.classList.remove('open');
  document.getElementById('resources-overlay')?.classList.remove('visible');

  const sidebar = document.getElementById('sidebar')!;
  const overlay = document.getElementById('sidebar-overlay')!;
  sidebar.classList.add('open');
  overlay.classList.add('visible');
  document.body.classList.add('sidebar-open');
}

/** Close sidebar */
export function closeSidebar(onClose?: () => void) {
  const sidebar = document.getElementById('sidebar')!;
  const overlay = document.getElementById('sidebar-overlay')!;
  sidebar.classList.remove('open');
  overlay.classList.remove('visible');
  document.body.classList.remove('sidebar-open');
  currentCountry = null;
  clearCountryURL();
  onClose?.();
}

/** Render full sidebar HTML */
function renderSidebar() {
  if (!currentCountry) return;
  const c = currentCountry;
  const status = STATUS_CONFIG[c.regulation_status];
  const sidebar = document.getElementById('sidebar')!;

  sidebar.innerHTML = `
    <div class="sidebar-header">
      <div class="sidebar-country">
        <span class="sidebar-flag">${c.flag}</span>
        <div class="sidebar-country-info">
          <h2>${c.name}</h2>
          <span class="sidebar-status-badge ${c.regulation_status}">
            <span class="status-dot"></span>
            ${status.label}
          </span>
        </div>
      </div>
      ${isAdminMode() ? '<button class="sidebar-edit-btn" aria-label="Edit country">Edit</button>' : ''}
      <button class="sidebar-close" aria-label="Close sidebar">&times;</button>
    </div>
    <div class="sidebar-tabs" role="tablist">
      ${TABS.map((tab, i) => `
        <button class="sidebar-tab${i === activeTab ? ' active' : ''}" data-tab="${i}" role="tab" aria-selected="${i === activeTab ? 'true' : 'false'}">${tab}</button>
      `).join('')}
    </div>
    <div class="sidebar-content">
      ${renderOverviewTab(c)}
      ${renderRegulationsTab(c)}
      ${renderContactsTab(c)}
      ${renderResourcesTab(c)}
      ${renderTendersTab(c)}
    </div>
  `;

  // Wire close button
  sidebar.querySelector('.sidebar-close')!.addEventListener('click', () => closeSidebar());

  // Wire tab switching
  sidebar.querySelectorAll('.sidebar-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      activeTab = parseInt((btn as HTMLElement).dataset.tab || '0');
      updateTabs(sidebar);
    });
  });

  // Wire admin Edit button
  const editBtn = sidebar.querySelector('.sidebar-edit-btn');
  if (editBtn && currentCountry) {
    editBtn.addEventListener('click', async () => {
      const { renderEditMode } = await import('../admin/editor');
      renderEditMode(currentCountry!, sidebar);
    });
  }

  // Wire clickable stat cards AND inline tab-links that navigate to tabs
  sidebar.querySelectorAll('.stat-card[data-goto-tab], a[data-goto-tab]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      activeTab = parseInt((el as HTMLElement).dataset.gotoTab || '0');
      updateTabs(sidebar);
    });
  });

  // Wire General Rules accordion toggle (regulations tab — all countries)
  sidebar.querySelectorAll('.reg-rules-header').forEach((btn) => {
    btn.addEventListener('click', () => {
      const section = btn.closest('.reg-rules-section')!;
      const isOpen = section.classList.toggle('open');
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  });

  // Wire US state accordion toggles (regulations tab)
  sidebar.querySelectorAll('.state-accordion-header').forEach((btn) => {
    btn.addEventListener('click', () => {
      const item = btn.closest('.state-accordion-item')!;
      const isOpen = item.classList.contains('open');
      // Close all others
      sidebar.querySelectorAll('.state-accordion-item.open').forEach((el) => {
        if (el !== item) el.classList.remove('open');
        el.querySelector('.state-accordion-header')?.setAttribute('aria-expanded', 'false');
      });
      // Toggle this one
      item.classList.toggle('open', !isOpen);
      btn.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
    });
  });

  // Wire fixer / operator state accordion toggles (contacts tab)
  // Markup for these headers comes from country data, so initialise aria-expanded here.
  sidebar.querySelectorAll('.fixer-state-header').forEach((btn) => {
    const item0 = btn.closest('.fixer-state-item');
    btn.setAttribute('aria-expanded', item0?.classList.contains('open') ? 'true' : 'false');
    btn.addEventListener('click', () => {
      const item = btn.closest('.fixer-state-item')!;
      const isOpen = item.classList.contains('open');
      // Close all others in the same accordion
      const accordion = item.closest('.fixer-state-accordion')!;
      accordion.querySelectorAll('.fixer-state-item.open').forEach((el) => {
        if (el !== item) el.classList.remove('open');
        el.querySelector('.fixer-state-header')?.setAttribute('aria-expanded', 'false');
      });
      item.classList.toggle('open', !isOpen);
      btn.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
    });
  });

  // Turn phone-number-like text into tel: links (static contact + fixer cards)
  linkifyPhoneNumbers(sidebar);

  // Async: populate Tenders tab
  if (currentCountry) hydrateTendersTab(sidebar, currentCountry);

  // Async: populate Featured Partners at the top of the contacts tab. Orgs that
  // link back to the map get top placement; renders nothing when the country
  // has none (the normal state).
  const fpContainer = sidebar.querySelector('#featured-partners-section') as HTMLElement | null;
  if (fpContainer) {
    const iso2 = fpContainer.dataset.iso2 || '';
    fetchFeaturedPartners(iso2).then((partners) => {
      if (!partners.length) { fpContainer.remove(); return; }
      const cards = partners.map(p => `
        <div class="contact-card" style="border:1px solid rgba(52,211,153,0.35);background:rgba(52,211,153,0.05);display:flex;align-items:center;gap:14px;">
          ${p.logo ? `<img src="${p.logo}" alt="${p.org} logo" style="max-height:40px;max-width:110px;object-fit:contain;flex-shrink:0;" loading="lazy">` : ''}
          <div>
            <div class="contact-card-title"><a href="${p.url}" target="_blank" rel="noopener" style="color:var(--color-accent);text-decoration:none;">${p.org}</a></div>
            ${p.blurb ? `<p style="font-size:12.5px;margin:2px 0 0;">${p.blurb}</p>` : ''}
          </div>
        </div>
      `).join('');
      fpContainer.innerHTML = `
        <div class="section-title">Featured partners</div>
        ${cards}
      `;
    });
  }

  // Async: populate Approved Training Organisations in contacts tab
  const atoContainer = sidebar.querySelector('#ato-section') as HTMLElement | null;
  if (atoContainer) {
    const iso2 = atoContainer.dataset.iso2 || '';
    fetchATOs(iso2).then((atos) => {
      if (!atos.length) { atoContainer.remove(); return; }
      const single = atos.length === 1;
      const atoCards = atos.map(a => `
        <div class="ato-card">
          <div class="ato-card-name">${a.url
            ? `<a href="${a.url}" target="_blank" rel="noopener">${a.name}</a>`
            : a.name}</div>
          ${a.city ? `<div class="ato-card-city">${a.city}</div>` : ''}
          ${a.email ? `<div class="contact-row" style="margin-top:6px">
            <svg class="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            <a href="mailto:${a.email}">${a.email}</a>
          </div>` : ''}
          ${a.phone ? `<div class="contact-row">
            <svg class="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
            <span>${a.phone}</span>
          </div>` : ''}
          ${a.approval_ref ? `<div class="ato-ref">Ref: ${a.approval_ref}</div>` : ''}
        </div>
      `).join('');

      if (single) {
        atoContainer.innerHTML = `
          <div class="section-title" style="margin-top:20px">Approved Training Organisations</div>
          ${atoCards}
        `;
      } else {
        atoContainer.innerHTML = `
          <div class="section-title" style="margin-top:20px">Approved Training Organisations</div>
          <div class="ato-accordion">
            <button class="ato-accordion-header" type="button" aria-expanded="false">
              <span>${atos.length} approved training organisations</span>
              <span class="ato-chevron">▸</span>
            </button>
            <div class="ato-accordion-body">
              ${atoCards}
            </div>
          </div>
        `;
        // Wire toggle
        const btn = atoContainer.querySelector('.ato-accordion-header')!;
        btn.addEventListener('click', () => {
          const isOpen = atoContainer.querySelector('.ato-accordion')!.classList.toggle('open');
          btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });
      }
      // Turn phone-number-like text in the newly-rendered ATO cards into tel: links.
      linkifyPhoneNumbers(atoContainer);
    });
  }

  // Async: populate Drones For Good projects + update overview badge
  const dfgContainer = sidebar.querySelector('#dfg-projects-list') as HTMLElement | null;
  if (dfgContainer) {
    const iso2 = dfgContainer.dataset.iso2 || '';
    getProjectsByCountry(iso2).then((projects) => {
      // Update overview Quick Info badge
      const badge = sidebar.querySelector('#dfg-count-badge') as HTMLElement | null;
      if (badge) {
        badge.textContent = projects.length > 0 ? `${projects.length}` : 'None';
        badge.className = 'stat-value';
      }

      if (!projects.length) {
        dfgContainer.remove();
        return;
      }
      // Hide the empty-state if it exists and we have DFG projects
      const emptyState = sidebar.querySelector('#resources-empty-state');
      if (emptyState) emptyState.remove();

      const html = projects.map((p: DroneProject) => {
        const cfg = PROJECT_TYPE_CONFIG[p.type] || { color: '#94a3b8', label: 'Project', icon: '' };
        return `
          <a href="${p.url}" target="_blank" rel="noopener" class="resource-item">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${cfg.color};flex-shrink:0;margin-right:4px;box-shadow:0 0 4px ${cfg.color}80;"></span>
            <div class="resource-item-info">
              <span class="resource-label">${p.name}</span>
              <span class="resource-desc">${cfg.icon} ${cfg.label} · ${p.org}</span>
            </div>
          </a>
        `;
      }).join('');

      dfgContainer.innerHTML = `
        <div class="section-title" style="margin-top:24px">Drones For Good Projects</div>
        <p class="country-companies-intro">Humanitarian, conservation, and community drone projects in this country.</p>
        ${html}
      `;
    });
  }
}

/** Switch visible tab */
function updateTabs(sidebar: HTMLElement) {
  sidebar.querySelectorAll('.sidebar-tab').forEach((btn, i) => {
    const isActive = i === activeTab;
    btn.classList.toggle('active', isActive);
    btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });
  sidebar.querySelectorAll('.tab-panel').forEach((panel, i) => {
    panel.classList.toggle('active', i === activeTab);
  });
  // Reset scroll position on tab change so the new tab starts at the top.
  const scroller = sidebar.querySelector('.sidebar-content') as HTMLElement | null;
  if (scroller) scroller.scrollTop = 0;
}

/** Phone-number-like pattern: a digit run with common separators, at least 9 digits. */
const PHONE_RE = /(\+?\d[\d\s\-()/]{7,}\d)/g;

/** Build a tel: href from a raw phone string (strip spaces/parens, keep leading +). */
function telHref(raw: string): string {
  const hasPlus = raw.trim().startsWith('+');
  const digits = raw.replace(/[^\d]/g, '');
  return `tel:${hasPlus ? '+' : ''}${digits}`;
}

/**
 * Wrap phone-number-like text in tel: links inside contact/fixer/ATO cards.
 * Walks TEXT NODES only, so existing anchors are never touched. A "0166… / 99…"
 * pattern is split so each side becomes its own tel: link.
 */
function linkifyPhoneNumbers(root: HTMLElement): void {
  const selectors = '.contact-card, .ato-card, .fixer-entry';
  root.querySelectorAll(selectors).forEach((card) => {
    const walker = document.createTreeWalker(card, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    let n = walker.nextNode();
    while (n) {
      // Skip text already inside an anchor.
      if (!(n.parentElement && n.parentElement.closest('a'))) textNodes.push(n as Text);
      n = walker.nextNode();
    }
    for (const node of textNodes) {
      const text = node.nodeValue || '';
      if (!PHONE_RE.test(text)) continue;
      PHONE_RE.lastIndex = 0;
      const frag = document.createDocumentFragment();
      let lastIdx = 0;
      let m: RegExpExecArray | null;
      while ((m = PHONE_RE.exec(text)) !== null) {
        const match = m[0];
        const digitCount = (match.match(/\d/g) || []).length;
        // Conservative: need at least 9 digits to treat as a phone number.
        if (digitCount < 9) continue;
        if (m.index > lastIdx) frag.appendChild(document.createTextNode(text.slice(lastIdx, m.index)));
        // A "num / num" pattern is two numbers, split on the slash.
        const parts = match.split('/');
        parts.forEach((part, i) => {
          const trimmed = part.trim();
          const partDigits = (trimmed.match(/\d/g) || []).length;
          if (partDigits >= 9) {
            const a = document.createElement('a');
            a.href = telHref(trimmed);
            a.textContent = trimmed;
            frag.appendChild(a);
          } else {
            frag.appendChild(document.createTextNode(trimmed));
          }
          if (i < parts.length - 1) frag.appendChild(document.createTextNode(' / '));
        });
        lastIdx = m.index + match.length;
      }
      if (lastIdx < text.length) frag.appendChild(document.createTextNode(text.slice(lastIdx)));
      node.parentNode?.replaceChild(frag, node);
    }
  });
}

/** Format boolean-like values for display */
function boolDisplay(val: unknown): { text: string; cls: string } {
  if (val === true || val === 'Yes') return { text: 'Yes', cls: 'yes' };
  if (val === false || val === 'No') return { text: 'No', cls: 'no' };
  if (typeof val === 'string' && val.length > 0) return { text: val, cls: 'varies' };
  return { text: 'N/A', cls: 'na' };
}

/** Extract a clean short authority name from the raw data */
function getCleanAuthorityName(raw: string): string {
  if (!raw) return 'N/A';
  let s = raw.split(/\n/)[0];

  // "X Drone Regulator: Authority Name ..." — extract what's after the ":" (handles both
  // "Drone Regulator in X:" and "Ireland Drone Regulator:" styles)
  const regulatorMatch = /[Dd]rone [Rr]egulat\w+[^:]+:\s*(.+)/.exec(s);
  if (regulatorMatch) s = regulatorMatch[1];

  s = s
    .split(' / ')[0]                                                           // first: discard " / alternate-number" suffixes (e.g. Ivory Coast "00 / 01WhatsApp…")
    .replace(/https?:\/\/\S+/g, '')                                           // remove URLs
    .replace(/[\w.-]+@[\w.-]+\.\w+/g, '')                                     // remove emails
    .replace(/@\S+/g, '')                                                      // remove leftover @domain fragments (e.g. Burundi double-email edge case)
    .replace(/\(?\+?\s?\d[\d\s()-]{5,}/g, '')                                 // phones incl. "(+ NNN)" format
    .replace(/\s*\(\d{1,5}\)\s*/g, ' ')                                        // remove isolated area-code remnants like "(242)" left after email regex consumed the rest
    .replace(/\b(Tel|Mobile|Fax|E-?[Mm]ail|Phone|WhatsApp)\s*:/g, '')         // contact labels
    .replace(/\(?(?:Website|currently offline|no website|offline)[^)]*\)?/gi, '') // offline / no-website notes e.g. "(Website offline)", "(No Website)", "currently offline)"
    .replace(/(NOTE|Link to|Address|UAS Info)[:\s].*/i, '')                   // other supplementary info after name
    .replace(/\)\s*[A-Z]\w+.*/g, ')')                                          // strip appended content after ")" e.g. "(DCA)Link…"
    .replace(/[\s:/\-,]+$/, '')                                               // strip trailing :/-space (NOT closing parens — they're part of names)
    .replace(/\s{2,}/g, ' ')
    .trim();

  return s.substring(0, 80) || 'N/A';
}

/**
 * Generate a real human-readable summary from the regulation data,
 * NOT just a truncation of the full regulation text.
 */
function generateSummary(c: CountryData): string {
  const status = STATUS_CONFIG[c.regulation_status];
  const s = c.regulations.summary.toLowerCase();
  const parts: string[] = [];

  // Opening line about ease of flight
  if (c.regulation_status === 'very_difficult') {
    parts.push(`Flying drones in ${c.name} is <strong style="color:${status.color}">very difficult</strong> — permits are rarely granted to foreigners, or enforcement is unpredictable.`);
  } else if (c.regulation_status === 'straightforward') {
    parts.push(`Flying drones in ${c.name} is <strong style="color:${status.color}">straightforward</strong> — clear processes and obtainable permits.`);
  } else if (c.regulation_status === 'complicated') {
    parts.push(`Flying drones in ${c.name} is <strong style="color:${status.color}">complicated</strong> — bureaucracy, unclear processes, or restrictions that make it hard without local help.`);
  } else {
    parts.push(`There is <strong>no reliable data</strong> on flying drones in ${c.name} at this time.`);
  }

  // Key requirements
  const reqs: string[] = [];
  if (s.includes('registration') && !s.includes('no registration')) reqs.push('registration');
  if (s.includes('license') || (s.includes('permit') && !s.includes('no permit'))) reqs.push('a license or permit');
  if (s.includes('insurance')) reqs.push('insurance');
  if (reqs.length > 0) {
    parts.push(`Pilots may need ${reqs.join(', ')}.`);
  }

  // Flight rules
  const rules: string[] = [];
  if (s.includes('line of sight') || s.includes('vlos') || s.includes('eyesight')) rules.push('visual line-of-sight (VLOS)');
  if (s.includes('daylight') || (s.includes('night') && (s.includes('no night') || s.includes('not at night')))) rules.push('daytime-only flying');
  if (/150\s*m|400\s*ft|120\s*m|500\s*ft/i.test(s)) {
    const altMatch = s.match(/(\d+)\s*(m|meters?|ft|feet)/i);
    if (altMatch) rules.push(`max altitude of ${altMatch[1]}${altMatch[2]}`);
  }
  if (rules.length > 0) {
    parts.push(`Key rules include ${rules.join(', ')}.`);
  }

  // Authority
  const auth = getCleanAuthorityName(c.aviation_authority.name || '');
  if (auth !== 'N/A') {
    parts.push(`The aviation authority is <strong>${auth}</strong>.`);
  }

  // Fallback if no useful info extracted
  if (parts.length <= 1 && c.regulations.summary.length > 0) {
    // Extract the first sentence from the raw summary as fallback
    const firstSentence = c.regulations.summary.split(/\.(?:\s|$)/)[0];
    if (firstSentence && firstSentence.length > 20) {
      parts.push(firstSentence.trim() + '.');
    }
  }

  return parts.join(' ');
}

function formatUpdatedDate(date: string): string {
  try {
    return new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch { return date; }
}

function renderOverviewTab(c: CountryData): string {
  const summaryLower = c.regulations.summary.toLowerCase();
  const cleanAuth = getCleanAuthorityName(c.aviation_authority.name || '');
  const status = STATUS_CONFIG[c.regulation_status];

  // Regulation indicators parsed from text
  const registrationFound = summaryLower.includes('registration') ? (!summaryLower.includes('no registration')) : null;
  const licenseFound = summaryLower.includes('license') || summaryLower.includes('permit') ? true : null;

  // Verified Pilot Available: true only when a real (non-placeholder) contact exists
  const hasPilotContact = hasRealPilotContact(c);

  // Only use intro_html in Overview summary if it's short (< 1000 chars — i.e. a real intro, not a full doc)
  const introHtmlForOverview = (c.regulations.intro_html && c.regulations.intro_html.length < 1000)
    ? c.regulations.intro_html : null;

  // Summary: first sentence (ease boilerplate) + condensed regulation detail
  const firstSentence = (() => {
    if (c.regulation_status === 'very_difficult')
      return `Flying drones in ${c.name} is <strong style="color:${status.color}">very difficult</strong> \u2014 permits are rarely granted to foreigners, or enforcement is unpredictable.`;
    if (c.regulation_status === 'straightforward')
      return `Flying drones in ${c.name} is <strong style="color:${status.color}">straightforward</strong> \u2014 clear processes and obtainable permits.`;
    if (c.regulation_status === 'complicated')
      return `Flying drones in ${c.name} is <strong style="color:${status.color}">complicated</strong> \u2014 bureaucracy, unclear processes, or restrictions that make it hard without local help.`;
    return `There is <strong>no reliable data</strong> on flying drones in ${c.name} at this time.`;
  })();

  // Condensed regulation detail \u2014 prefer short intro_html stripped text, fall back to key requirements
  const regDetail = (() => {
    if (introHtmlForOverview) {
      // intro_html is short enough \u2014 already shown as HTML above; don't duplicate in text
      return '';
    }
    if (c.regulations.intro_html) {
      // intro_html is too long to show in full \u2014 show a short excerpt
      const stripped = c.regulations.intro_html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      return stripped.length > 220 ? stripped.slice(0, 220).trimEnd() + '\u2026' : stripped;
    }
    const parts: string[] = [];
    const reqs: string[] = [];
    if (summaryLower.includes('registration') && !summaryLower.includes('no registration')) reqs.push('registration');
    if (summaryLower.includes('license') || (summaryLower.includes('permit') && !summaryLower.includes('no permit'))) reqs.push('a licence or permit');
    if (summaryLower.includes('insurance')) reqs.push('insurance');
    if (reqs.length > 0) parts.push(`Pilots may need ${reqs.join(', ')}.`);
    const auth = getCleanAuthorityName(c.aviation_authority.name || '');
    if (auth !== 'N/A') parts.push(`The aviation authority is <strong>${auth}</strong>.`);
    return parts.join(' ');
  })();

  return `
    <div class="tab-panel active" data-panel="0">
      <div class="section-title">Quick Info</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Status</div>
          <div class="stat-value" style="color:${status.color}">${status.label}</div>
        </div>
        <div class="stat-card clickable" data-goto-tab="2">
          <div class="stat-label">Authority</div>
          <div class="stat-value">${cleanAuth}</div>
        </div>
        <div class="stat-card clickable" data-goto-tab="1">
          ${c.iso2 === 'US' ? '<a href="https://faadronezone.faa.gov/" target="_blank" rel="noopener" class="stat-card-link">' : ''}
          ${c.iso2 === 'IN' ? '<a href="https://digitalsky.dgca.gov.in/" target="_blank" rel="noopener" class="stat-card-link">' : ''}
          <div class="stat-label">Registration</div>
          <div class="stat-value ${registrationFound === true ? 'varies' : registrationFound === false ? 'no' : 'na'}">${registrationFound === true ? 'Required' : registrationFound === false ? 'No' : 'See Details'}</div>
          ${c.iso2 === 'US' || c.iso2 === 'IN' ? '</a>' : ''}
        </div>
        <div class="stat-card clickable" data-goto-tab="1">
          <div class="stat-label">Licence / Permit</div>
          <div class="stat-value ${licenseFound === true ? 'varies' : 'na'}">${licenseFound === true ? 'Required' : 'See Details'}</div>
        </div>
        <div class="stat-card clickable" data-goto-tab="2">
          <div class="stat-label">Verified Pilots Available</div>
          <div class="stat-value">${hasPilotContact ? 'Yes' : 'Maybe'}</div>
        </div>
        <div class="stat-card clickable" data-goto-tab="3">
          <div class="stat-label" style="font-size:8.5px;letter-spacing:0.4px">Drones For Good Projects</div>
          <div class="stat-value" id="dfg-count-badge">\u2013</div>
        </div>
      </div>
      ${AUTH_SYSTEMS[c.iso2] ? `
        <a href="${AUTH_SYSTEMS[c.iso2].url}" target="_blank" rel="noopener" class="auth-banner" style="background:${AUTH_SYSTEMS[c.iso2].gradient}" data-glow="${AUTH_SYSTEMS[c.iso2].hoverGlow}">
          <span class="auth-banner-icon">${AUTH_SYSTEMS[c.iso2].icon}</span>
          <span class="auth-banner-text">${AUTH_SYSTEMS[c.iso2].description} <strong>${AUTH_SYSTEMS[c.iso2].label}</strong></span>
          <span class="auth-banner-arrow">\u2192</span>
        </a>
      ` : ''}
      <div class="section-title">Summary</div>
      <div class="regulation-content">
        ${introHtmlForOverview
          ? `${introHtmlForOverview}<p style="margin-top:8px"><a href="#" data-goto-tab="1">Full regulations \u2192</a></p>`
          : `<p>${firstSentence}${regDetail ? ` ${regDetail}` : ''} <a href="#" data-goto-tab="1">Full regulations \u2192</a></p>`
        }
        ${c.regulation_status === 'straightforward' ? `
          <p class="pilot-rec">We recommend flying with <a href="#" data-goto-tab="2">one of our verified local pilots</a> \u2014 they can simplify the process and may be required for commercial operations.</p>
        ` : c.regulation_status === 'complicated' ? `
          <p class="pilot-rec pilot-rec--warn">Regulations here are complex. We strongly recommend using <a href="#" data-goto-tab="2">one of our verified local pilots</a> to avoid delays and permit issues.</p>
        ` : c.regulation_status === 'very_difficult' ? `
          <p class="pilot-rec pilot-rec--danger">Regulations here are highly restrictive. We consider using <a href="#" data-goto-tab="2">one of our verified local pilots</a> essential \u2014 and urge great care before travelling with drone equipment.</p>
        ` : ''}
      </div>
      ${freshnessLine(c.regulations)}
      <div class="pilot-cta">
        <p class="pilot-cta-text">
          ${hasPilotContact
            ? `Are you a drone pilot in <strong>${c.name}</strong>? Expand the network.`
            : `No verified pilots listed yet for <strong>${c.name}</strong>. Be the first.`}
        </p>
        <a href="https://africandrone.org/contact-us" target="_blank" rel="noopener" class="pilot-cta-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          ${hasPilotContact ? `Join the Pilot Network in ${c.name}` : `Be a Verified Pilot in ${c.name}`}
        </a>
      </div>
    </div>
  `;
}

/** Sanitize HTML by parsing through DOMParser to auto-close unclosed tags */
function sanitizeHTML(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.innerHTML;
}

/** Markers that identify the generic pan-African fixer entries (not country-specific). */
const GENERIC_FIXER_MARKERS = ['africa-fixers-0828001b8', 'groups/530714863705089'];

/**
 * Split verified_fixers_html into country-specific and pan-African directory blocks.
 * Parses the HTML string DOM-safely via a template element, moves generic .fixer-entry
 * nodes into a separate section rendered after the country-specific fixers.
 * Returns the full HTML for the fixers card body.
 */
function renderFixersHTML(html: string): { body: string; hasSpecific: boolean } {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const entries = Array.from(tpl.content.querySelectorAll('.fixer-entry'));

  // No recognisable entries: render the source HTML unchanged, treat as country-specific.
  if (entries.length === 0) return { body: html, hasSpecific: true };

  const generic: Element[] = [];
  const specific: Element[] = [];
  for (const el of entries) {
    const inner = el.innerHTML || '';
    if (GENERIC_FIXER_MARKERS.some(m => inner.includes(m))) generic.push(el);
    else specific.push(el);
  }

  const genericBlock = generic.length
    ? `<div class="fixer-africa-wide">
         <div class="section-subtitle">Africa-wide directories</div>
         <p class="fixer-africa-note">Pan-African referral services, not country-specific contacts.</p>
         ${generic.map(el => el.outerHTML).join('')}
       </div>`
    : '';

  // All entries generic: show only the Africa-wide section (no country-specific label).
  if (specific.length === 0) return { body: genericBlock, hasSpecific: false };

  const specificBlock = specific.map(el => el.outerHTML).join('');
  return { body: `${specificBlock}${genericBlock}`, hasSpecific: true };
}

function renderRegulationsTab(c: CountryData): string {
  const hasContent = c.regulations.summary_html || c.regulations.summary;

  // For India, include aviation authority contact info in the regulations tab
  const showAuthorityInRegs = c.iso2 === 'IN';
  const hasAuthority = c.aviation_authority.name || c.aviation_authority.html;

  return `
    <div class="tab-panel" data-panel="1">
      ${c.regulations.quick_links?.length ? `
        <div class="reg-quick-links">
          ${c.regulations.quick_links.map(link => `
            <a href="${link.url}" target="_blank" rel="noopener" class="reg-link-btn">
              <span class="reg-link-btn-label">${link.label}</span>
              <svg class="reg-link-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </a>
          `).join('')}
        </div>
      ` : ''}
      ${c.regulations.intro_html ? `<div class="regulation-intro">${c.regulations.intro_html}</div>` : ''}
      <div class="rp-section reg-rules-section">
        <button class="rp-section-header reg-rules-header" type="button" aria-expanded="false">
          <span class="rp-section-icon">📋</span>
          <span class="rp-section-label">General Rules</span>
          <span class="rp-chevron">▸</span>
        </button>
        <div class="rp-section-body">
          ${hasContent ? `
            <div class="regulation-content">
              ${c.regulations.summary_html ? sanitizeHTML(c.regulations.summary_html) : `<p>${c.regulations.summary}</p>`}
            </div>
            ${c.regulations.source_urls.length > 0 ? `
              <div class="regulation-meta">
                Sources: ${c.regulations.source_urls.map(u => urlAsHostnameLink(u)).join(', ')}
              </div>
            ` : ''}
          ` : `
            <div class="empty-state">
              <p>No detailed regulation data available yet.</p>
              <p style="margin-top:8px">
                <a href="https://africandrone.org/drone-hotline" target="_blank" rel="noopener" style="color:var(--color-accent)">Suggest an update</a>
              </p>
            </div>
          `}
        </div>
      </div>
      ${showAuthorityInRegs && hasAuthority ? `
        <div class="section-title" style="margin-top:24px">Authority Contact Information</div>
        <div class="contact-card">
          ${c.aviation_authority.html || `<div class="contact-card-title">${c.aviation_authority.name}</div>`}
          ${c.aviation_authority.url ? `
            <div class="contact-row">
              <svg class="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
              <a href="${c.aviation_authority.url}" target="_blank" rel="noopener">${c.aviation_authority.url}</a>
            </div>
          ` : ''}
          ${c.aviation_authority.email ? `
            <div class="contact-row">
              <svg class="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              <a href="mailto:${c.aviation_authority.email}">${c.aviation_authority.email}</a>
            </div>
          ` : ''}
          ${c.aviation_authority.phone ? `
            <div class="contact-row">
              <svg class="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              <span>${c.aviation_authority.phone}</span>
            </div>
          ` : ''}
        </div>
      ` : ''}
      ${c.us_states && c.us_states.length > 0 ? renderUSStateRegulations(c.us_states) : ''}
      ${c.resources.length > 0 ? `
        <div class="section-title" style="margin-top:24px">Links &amp; Resources</div>
        ${c.resources.map(r => `
          <a href="${r.url}" target="_blank" rel="noopener" class="resource-item">
            <svg class="resource-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            <span class="resource-label">${r.title}</span>
          </a>
        `).join('')}
      ` : ''}
      ${freshnessLine(c.regulations)}
      <p class="reg-disclaimer">Regulations change often. This page is general reference, not legal advice. Verify with ${getCleanAuthorityName(c.aviation_authority.name || '')} before flying. <a href="/about.html">About this data</a></p>
      <div class="pilot-cta" style="border-color:rgba(200,150,62,0.4);background:#110e06;">
        <p class="pilot-cta-text">Have an update on drone regulations in <strong>${c.name}</strong>?</p>
        <a href="https://africandrone.org/drone-hotline" target="_blank" rel="noopener" class="pilot-cta-btn" style="background:#c8963e;color:#fff;box-shadow:0 2px 12px rgba(200,150,62,0.2);">
          Let us know →
        </a>
      </div>
    </div>
  `;
}

/** Render collapsible US state-by-state regulation links */
function renderUSStateRegulations(states: import('../types').USStateData[]): string {
  return `
    <div class="section-title" style="margin-top:24px">State-by-State Regulations</div>
    <p class="state-regs-intro">Click a state to view its drone statutes, local ordinances, university policies, and AAM regulations.</p>
    <div class="state-accordion">
      ${states.map(state => {
        const totalLinks = state.categories.reduce((sum, cat) => sum + cat.links.length, 0);
        return `
          <div class="state-accordion-item">
            <button class="state-accordion-header" data-state="${state.code}" aria-expanded="false">
              <span class="state-accordion-name">${state.name}</span>
              <span class="state-accordion-meta">${totalLinks} link${totalLinks !== 1 ? 's' : ''}</span>
              <svg class="state-accordion-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <div class="state-accordion-body">
              ${state.categories.map(cat => `
                <div class="state-category">
                  <div class="state-category-title">${cat.name}</div>
                  <div class="state-category-links">
                    ${cat.links.map(link => `
                      <a href="${link.url}" target="_blank" rel="noopener" class="state-link">
                        <svg class="state-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        <span>${link.title}</span>
                      </a>
                    `).join('')}
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

function renderContactsTab(c: CountryData): string {
  // For India, authority info is shown in regulations tab instead
  const hasAuthority = c.iso2 !== 'IN' && (c.aviation_authority.name || c.aviation_authority.html);
  const hasPilots = c.contacts.verified_pilots || c.contacts.verified_pilots_html;
  const hasFixers = c.contacts.verified_fixers || c.contacts.verified_fixers_html;
  const hasSocial = c.contacts.social_media || c.contacts.social_media_html;
  const hasWhatsApp = c.contacts.whatsapp;
  const hasAny = hasAuthority || hasPilots || hasFixers || hasSocial || hasWhatsApp;

  // Split country-specific fixers from pan-African directories at render time.
  const fixers = c.contacts.verified_fixers_html
    ? renderFixersHTML(c.contacts.verified_fixers_html)
    : { body: '<p>' + c.contacts.verified_fixers + '</p>', hasSpecific: true };

  return `
    <div class="tab-panel" data-panel="2">
      <div id="featured-partners-section" data-iso2="${c.iso2}"></div>
      ${!hasAny ? `
        <div class="empty-state">
          <p>No contact information available yet.</p>
          <p style="margin-top:8px">
            <a href="https://africandrone.org/drone-hotline" target="_blank" rel="noopener" style="color:var(--color-accent)">Add contacts</a>
          </p>
        </div>
      ` : ''}
      ${hasAuthority ? `
        <div class="section-title">Aviation Authority</div>
        <div class="contact-card">
          ${c.aviation_authority.html || `<div class="contact-card-title">${c.aviation_authority.name}</div>`}
          ${c.aviation_authority.url ? `
            <div class="contact-row">
              <svg class="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
              <a href="${c.aviation_authority.url}" target="_blank" rel="noopener">${c.aviation_authority.url}</a>
            </div>
          ` : ''}
          ${c.aviation_authority.email ? `
            <div class="contact-row">
              <svg class="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              <a href="mailto:${c.aviation_authority.email}">${c.aviation_authority.email}</a>
            </div>
          ` : ''}
          ${c.aviation_authority.phone ? `
            <div class="contact-row">
              <svg class="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              <span>${c.aviation_authority.phone}</span>
            </div>
          ` : ''}
        </div>
      ` : ''}
      <div id="ato-section" data-iso2="${c.iso2}"></div>
      ${hasPilots ? `
        <div class="section-title">Verified Pilot Contacts</div>
        <div class="contact-card">
          ${c.contacts.verified_pilots_html || '<p>' + c.contacts.verified_pilots + '</p>'}
        </div>
      ` : `
        <div class="section-title">Verified Pilot Contacts</div>
        <div class="contact-card">
          <p>Join the <a href="https://chat.whatsapp.com/EA0Yd3hkWKX3KlEKywmw2L" target="_blank" rel="noopener" style="color:var(--color-accent)">africanDRONE WhatsApp Community</a> for the latest pilot availability in every country.</p>
        </div>
      `}
      ${hasFixers ? `
        ${fixers.hasSpecific ? '<div class="section-title">Verified Field Producers / Fixers</div>' : ''}
        <div class="contact-card">
          ${fixers.body}
        </div>
      ` : ''}
      ${hasSocial ? `
        <div class="section-title">Social Media</div>
        <div class="contact-card">
          ${c.contacts.social_media_html || '<p>' + c.contacts.social_media + '</p>'}
        </div>
      ` : ''}
      ${hasWhatsApp ? `
        <div class="section-title">Community</div>
        <a href="${c.contacts.whatsapp}" target="_blank" rel="noopener" class="resource-item whatsapp-cta">
          <svg class="resource-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.496A11.96 11.96 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.291 0-4.398-.762-6.098-2.047l-.424-.336-3.091 1.036 1.036-3.091-.336-.424A9.958 9.958 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
          <span class="resource-label">africanDRONE WhatsApp Community</span>
          <span class="whatsapp-cta-arrow">→</span>
        </a>
      ` : ''}
      <div class="pilot-cta">
        <p class="pilot-cta-text">
          ${hasPilots
            ? `Are you a drone pilot in <strong>${c.name}</strong>? Expand the network.`
            : `No verified pilots listed yet for <strong>${c.name}</strong>. Be the first.`}
        </p>
        <a href="https://africandrone.org/contact-us" target="_blank" rel="noopener" class="pilot-cta-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          ${hasPilots ? `Join the Pilot Network in ${c.name}` : `Be a Verified Pilot in ${c.name}`}
        </a>
      </div>
    </div>
  `;
}

function renderResourcesTab(c: CountryData): string {
  // Get companies headquartered in this country from the resources database
  const hqCompanies = resourcesByCountry(c.iso2);

  const hasResources = c.resources.length > 0;
  const hasCompanies = hqCompanies.length > 0;

  if (!hasCompanies) {
    return `
      <div class="tab-panel" data-panel="3">
        <div id="dfg-projects-list" data-iso2="${c.iso2}"></div>
        <div class="empty-state" id="resources-empty-state">
          <p>No resources listed yet.</p>
          <p style="margin-top:8px">
            <a href="https://africandrone.org/drone-hotline" target="_blank" rel="noopener" style="color:var(--color-accent)">Suggest a resource</a>
          </p>
        </div>
        <div class="pilot-cta" style="border-color:rgba(200,150,62,0.25);background:rgba(200,150,62,0.06);">
          <p class="pilot-cta-text">Want your organisation or project featured on the map?</p>
          <a href="https://africandrone.org/contact-us" target="_blank" rel="noopener" class="pilot-cta-btn" style="background:#c8963e;color:#fff;box-shadow:0 2px 12px rgba(200,150,62,0.2);">
            Get in touch →
          </a>
        </div>
      </div>
    `;
  }

  return `
    <div class="tab-panel" data-panel="3">
      ${hasCompanies ? `
        <div class="section-title" ${hasResources ? 'style="margin-top:24px"' : ''}>Companies & Organizations</div>
        <p class="country-companies-intro">Drone industry companies headquartered in ${c.name}.</p>
        ${hqCompanies.map(company => `
          <a href="${company.url}" target="_blank" rel="noopener" class="resource-item">
            <svg class="resource-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
            <div class="resource-item-info">
              <span class="resource-label">${company.name}</span>
              ${company.description ? `<span class="resource-desc">${company.description}</span>` : ''}
            </div>
          </a>
        `).join('')}
      ` : ''}
      <div id="dfg-projects-list" data-iso2="${c.iso2}"></div>
      <div class="pilot-cta" style="border-color:rgba(200,150,62,0.25);background:rgba(200,150,62,0.06);">
        <p class="pilot-cta-text">Want your organisation or project featured on the map?</p>
        <a href="https://africandrone.org/contact-us" target="_blank" rel="noopener" class="pilot-cta-btn" style="background:#c8963e;color:#fff;box-shadow:0 2px 12px rgba(200,150,62,0.2);">
          Get in touch →
        </a>
      </div>
    </div>
  `;
}

/** ── TENDERS TAB ──────────────────────────────────────────────────────────
 *  Lists active RFPs for this country. Loads opportunities.json on demand;
 *  each tender is a click-to-expand accordion card.
 *  Country-name match is case-insensitive with aliasing (DRC ↔ DR Congo etc.). */
function renderTendersTab(c: CountryData): string {
  return `
    <div class="tab-panel" data-panel="4">
      <div id="tenders-list" data-country="${c.name}">
        <div class="rp-news-loading" style="padding:8px 0;">
          <div class="rp-news-skeleton"></div>
          <div class="rp-news-skeleton"></div>
          <div class="rp-news-skeleton"></div>
        </div>
      </div>
    </div>
  `;
}

/** Hydrate the Tenders tab — called after sidebar HTML is mounted. */
async function hydrateTendersTab(sidebar: HTMLElement, country: CountryData): Promise<void> {
  const container = sidebar.querySelector('#tenders-list') as HTMLElement | null;
  if (!container) return;
  const all = await loadAllOpportunities();
  const matches = tendersForCountry(all, country.name);

  if (matches.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <p>No active tenders found for ${country.name}.</p>
        <p style="margin-top:8px;font-size:12px;color:var(--color-text-dim)">
          We scan UNGM, World Bank, AfDB, TED Europa, ReliefWeb, AWF, SA eTenders + more daily.
          ${isAfricanCountry(country.name)
            ? 'New African tenders show up here as they get posted.'
            : ''}
        </p>
      </div>
    `;
    return;
  }

  // Chronological asc by deadline, no-deadline at bottom
  matches.sort((a, b) => {
    const ad = a.deadline || '';
    const bd = b.deadline || '';
    if (!ad && !bd) return 0;
    if (!ad) return 1;
    if (!bd) return -1;
    return ad.localeCompare(bd);
  });

  const today = new Date();
  const fmtDl = (iso: string): { label: string; tone: 'soon'|'ok'|'unknown' } => {
    if (!iso) return { label: 'Open / no deadline', tone: 'unknown' };
    try {
      const d = new Date(iso + 'T00:00:00');
      const days = Math.round((d.getTime() - today.getTime()) / 86_400_000);
      const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const suffix = days <= 0 ? ' (today)' : days === 1 ? ' (tomorrow)' :
                     days <= 30 ? ` (${days} days)` : '';
      return { label: label + suffix, tone: (days <= 7 ? 'soon' : 'ok') };
    } catch { return { label: iso, tone: 'unknown' }; }
  };

  container.innerHTML = `
    <div class="section-title">
      ${matches.length} active ${matches.length === 1 ? 'tender' : 'tenders'} for ${country.name}
    </div>
    <div class="country-tenders-list">
      ${matches.map((o, i) => {
        const dl = fmtDl(o.deadline);
        return `
          <div class="country-tender-item" data-idx="${i}">
            <button class="country-tender-header" type="button" aria-expanded="false">
              <div class="country-tender-headline">
                <span class="country-tender-deadline tone-${dl.tone}">⏱ ${dl.label}</span>
                <span class="country-tender-title">${o.title}</span>
              </div>
              <span class="country-tender-chevron">▸</span>
            </button>
            <div class="country-tender-body">
              <div class="country-tender-meta">
                ${o.issuer ? `<span>🏛 ${o.issuer}</span>` : ''}
                <span class="country-tender-source">${o.source}</span>
                ${o.category ? `<span class="rp-opp-category" style="margin-left:auto">${o.category}</span>` : ''}
              </div>
              ${o.summary ? `<p class="country-tender-summary">${o.summary}</p>` : ''}
              <a class="country-tender-link" href="${o.url}" target="_blank" rel="noopener">
                View notice →
              </a>
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `;

  // Wire accordion expand/collapse
  container.querySelectorAll('.country-tender-header').forEach(btn => {
    btn.addEventListener('click', () => {
      const item = btn.closest('.country-tender-item')!;
      const isOpen = item.classList.contains('open');
      // Close siblings — match the pattern used elsewhere in sidebar
      container.querySelectorAll('.country-tender-item.open').forEach(el => {
        if (el !== item) el.classList.remove('open');
        el.querySelector('.country-tender-header')?.setAttribute('aria-expanded', 'false');
      });
      item.classList.toggle('open', !isOpen);
      btn.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
    });
  });
}

/** Re-render the sidebar in view mode (used by admin editor after save/cancel) */
export function refreshSidebar(): void {
  if (currentCountry) renderSidebar();
}

/** Get the currently displayed country */
export function getCurrentCountry(): CountryData | null {
  return currentCountry;
}

/** Truncate text with ellipsis */
function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).trimEnd() + '...';
}
